import { Fragment, type KeyboardEvent, useMemo } from 'react'

import { ButtonRoot } from '@/components/common/Button'
import { LoadingMask } from '@/components/common/LoadingMask'
import { NoResults } from '@/components/common/NoResults'
import { type TypeaheadOptionGroupType } from '@/defs/input'
import { cn } from '@/utils/commonUtil'

import styles from './InputTypeaheadContentList.module.scss'

export type Props<T extends string = string> = {
  groups: TypeaheadOptionGroupType<T>[]
  loading?: boolean
  onKeyDownButton: (i: number, e: KeyboardEvent<HTMLElement>) => void
  onSelect: (selected: T) => void
  setRefButton: (i: number, element: HTMLElement | null) => void
  value?: T
}

export const InputTypeaheadContentList = <T extends string = string>({
  groups,
  loading,
  onKeyDownButton,
  onSelect,
  setRefButton,
  value,
}: Props<T>) => {
  const groupsWithOffset = useMemo(
    () =>
      groups.reduce<({ offset: number } & TypeaheadOptionGroupType<T>)[]>((acc, group) => {
        const previous = acc.at(-1)
        acc.push({ ...group, offset: previous ? previous.offset + previous.options.length : 0 })
        return acc
      }, []),
    [groups],
  )

  return (
    <div className={styles.dropdown}>
      {groupsWithOffset.length > 0 ? (
        groupsWithOffset.map(({ Component = Fragment, offset, options }, i) => (
          <Component key={i}>
            {options.map(({ Component = Fragment, label, labelDisplay, value: optionValue }, j) => (
              <ButtonRoot
                className={cn(styles.item, { selected: value === optionValue, styles })}
                key={optionValue}
                onClick={() => onSelect(optionValue)}
                onKeyDown={(e) => onKeyDownButton(offset + j, e)}
                onMouseDown={(e) => e.preventDefault()}
                ref={(el) => setRefButton(offset + j, el)}
              >
                <Component>
                  <span className={styles.itemLabel}>{labelDisplay ?? label}</span>
                </Component>
              </ButtonRoot>
            ))}
          </Component>
        ))
      ) : loading ? null : (
        <NoResults />
      )}
      <LoadingMask blurNone loading={loading} />
    </div>
  )
}
